import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type MandantZeile } from '../lib/api'
import { datum } from '../lib/format'
import { Fenster } from '../teile/Fenster'
import { Filterfeld } from '../teile/Filterfeld'
import { artParameter, pfad, useArt } from '../lib/art'

const spalten = { gridTemplateColumns: '90px minmax(0, 1fr) 90px 110px 90px 130px' }
// Kreditkarten ohne die Spalte Konten: die zaehlt Bankkonten.
const spaltenKarte = { gridTemplateColumns: '90px minmax(0, 1fr) 110px 90px 130px' }

export function MandantenSeite() {
  const [suche, setzeSuche] = useState('')
  const [fensterOffen, setzeFensterOffen] = useState(false)
  const art = useArt()
  const karte = art === 'kreditkarte'
  const raster = karte ? spaltenKarte : spalten

  const { data, isPending, error } = useQuery({
    queryKey: ['mandanten', art, suche],
    queryFn: () => api.holen<{ mandanten: MandantZeile[] }>(`/api/mandanten?${artParameter(art)}${suche ? `&suche=${encodeURIComponent(suche)}` : ''}`),
  })

  return (
    <>
      <div className="listenleiste">
        <Filterfeld className="suchfeld" wert={suche} setzeWert={setzeSuche} beschriftung="Mandanten filtern" platzhalter="Name, Nummer oder IBAN" />
        <button type="button" className="knopf" onClick={() => setzeFensterOffen(true)}>Mandant anlegen</button>
      </div>

      <div className="liste">
        <div className="listenkopf" style={raster}>
          <div>Nummer</div>
          <div>Name</div>
          {!karte && <div className="rechts">Konten</div>}
          <div className="rechts">Zu prüfen</div>
          <div className="rechts">Bereit</div>
          <div>Letzter Export</div>
        </div>

        {isPending && <div className="laedt">Wird geladen …</div>}
        {error && (
          <div className="leer">
            <strong>Die Mandanten konnten nicht geladen werden.</strong>
            {error instanceof Error ? error.message : ''}
          </div>
        )}
        {data && data.mandanten.length === 0 && (
          <div className="leer">
            {suche ? (
              <strong>Kein Mandant passt zu dieser Suche.</strong>
            ) : (
              <>
                <strong>Noch keine Mandanten angelegt.</strong>
                <p>Mandanten entstehen am schnellsten beim Zuordnen eines gelesenen Auszugs. Die IBAN wird dabei gemerkt, jeder weitere Auszug dieses Kontos ordnet sich selbst zu.</p>
              </>
            )}
          </div>
        )}

        {data?.mandanten.map((m) => (
          <Link key={m.id} to={pfad(art, `/mandanten/${m.id}`)} className="zeile" style={raster}>
            <div className="leiser zahl">{m.mandantennummer ?? '—'}</div>
            <div className="sache">{m.name}</div>
            {!karte && <div className="leise zahl rechts">{m.konten}</div>}
            <div className={`zahl rechts ${m.zu_pruefen > 0 ? 'text-warnung' : 'leiser'}`}>{m.zu_pruefen}</div>
            <div className={`zahl rechts ${m.bereit > 0 ? 'text-akzent' : 'leiser'}`}>{m.bereit}</div>
            <div className="leiser zahl">{m.letzter_export ? datum(m.letzter_export) : '—'}</div>
          </Link>
        ))}
      </div>

      <MandantAnlegen offen={fensterOffen} aufSchliessen={() => setzeFensterOffen(false)} />
    </>
  )
}

function MandantAnlegen({ offen, aufSchliessen }: { offen: boolean; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const navigiere = useNavigate()
  const art = useArt()
  const [name, setzeName] = useState('')
  const [nummer, setzeNummer] = useState('')
  const [fehler, setzeFehler] = useState<string | null>(null)

  const anlegen = useMutation({
    mutationFn: () => api.senden<{ id: string }>('/api/mandanten', { name: name.trim(), mandantennummer: nummer ? Number(nummer) : null }),
    onSuccess: async (antwort) => {
      await abfragen.invalidateQueries({ queryKey: ['mandanten'] })
      setzeName('')
      setzeNummer('')
      aufSchliessen()
      navigiere(pfad(art, `/mandanten/${antwort.id}`))
    },
    onError: (problem) => setzeFehler(problem instanceof ApiFehler ? problem.message : 'Der Mandant konnte nicht angelegt werden.'),
  })

  function absenden(e: FormEvent) {
    e.preventDefault()
    setzeFehler(null)
    anlegen.mutate()
  }

  return (
    <Fenster titel="Mandant anlegen" offen={offen} aufSchliessen={aufSchliessen}>
      <form className="formular" onSubmit={absenden}>
        {fehler && <div className="fehler">{fehler}</div>}
        <div>
          <label htmlFor="m-bezeichnung">Name</label>
          <input id="m-bezeichnung" type="text" required autoFocus autoComplete="off" data-1p-ignore value={name} onChange={(e) => setzeName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="m-nummer">Mandantennummer in DATEV</label>
          <input id="m-nummer" type="number" value={nummer} onChange={(e) => setzeNummer(e.target.value)} />
          <div className="hinweis">Optional. Steht im Dateinamen des Exports, damit er in DATEV sofort zuzuordnen ist.</div>
        </div>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="submit" className="knopf gefuellt" disabled={anlegen.isPending}>{anlegen.isPending ? 'Wird angelegt …' : 'Anlegen'}</button>
        </div>
      </form>
    </Fenster>
  )
}
